import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Injector, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpCacheClient } from '@common-modules/cache/http-cache/http-cache.client';
import { HttpCacheInterceptor } from '@common-modules/cache/http-cache/http-cache.interceptor';
import { HttpCacheManager } from '@common-modules/cache/http-cache/http-cache.manager';
import { HierarchyService } from '@common-modules/dependencies/he/hierarchy.service';
import { NotificationPopupService } from '@common-modules/dependencies/shared/notification-popup.service';
import { DynamicLayoutModule } from '@common-modules/dynamic-layout/dynamic-layout.module';
import { ReduxStateModule } from '@common-modules/redux/redux-state.module';
import { RightPanelModule } from '@common-modules/right-panel/right-panel.module';
import { AuthenticationService } from '@common-modules/shared/auth/services/authentication.service';
import { AuthorizeService } from '@common-modules/shared/auth/services/authorize.service';
import { TokenInterceptor } from '@common-modules/shared/auth/token.interceptor';
import { GridSettingsService } from '@common-modules/shared/core/grid/grid-settings.service';
import { ArrayHelperService } from '@common-modules/shared/helpers/array-helper.service';
import { ColorHelperService } from '@common-modules/shared/helpers/color-helper.service';
import { GridHelperService } from '@common-modules/shared/helpers/grid-helper.service';
import { NavMenuBuilderHelperService } from '@common-modules/shared/helpers/navmenu-builder-helper.service';
import { DatesInterceptor } from '@common-modules/shared/interceptors/dates.interceptor';
import { KendoMessageService } from '@common-modules/shared/localization/kendo-message.service';
import { RightPanelService } from '@common-modules/shared/navigation/right-panel.service';
import { NotificationSelectionService } from '@common-modules/shared/notifications/notification-selection.service';
import { PendingChangesManagerService } from '@common-modules/shared/pending-changes/services/pending-changes-manager.service';
import { UoMService } from '@common-modules/shared/uom/uom.service';
import { SpinnerInterceptor } from '@common-modules/wlm-spinner/spinner.interceptor';
import { WlmSpinnerModule } from '@common-modules/wlm-spinner/wlm-spinner.module';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { TranslateModule } from '@ngx-translate/core';
import { MessageService } from '@progress/kendo-angular-l10n';
import * as $ from 'jquery';
import { environment } from 'src/environments/environment';
import { HomeModule } from './features/home/home.module';
import { MapLayerSourcesService } from './features/map/map-layer-sources.service';
import { WlmNavigationModule } from './features/navigation/wlm-navigation.module';
import { HierarchyTreeFilterHelperService } from './features/shared/filters/hierarchy/hierarchy-tree-filter/hierarchy-tree-filter-helper.service';
import { WlmSpinnerSplashService } from './features/shared/services/wlm-spinner-splash.service';
import { WlmSharedModule } from './features/shared/wlm-shared.module';
import { WlmFiltersModule } from './features/wlm-filters/wlm-filters.module';

import { CommonModule } from '@angular/common';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { LeaksService } from '@common-modules/dependencies/alc/services/alc.leaks.service';
import { DynamicFormsModule } from '@common-modules/dynamic-forms/dynamic-forms.module';
import { DynamicFormParserService } from '@common-modules/dynamic-forms/services/dynamic-form-parser.service';
import { FormlyFormParserService } from '@common-modules/dynamic-forms/services/formly-form-parser.service';
import { CommonNavigationModule } from '@common-modules/navigation/common-navigation.module';
import { WlmNavigationService } from '@common-modules/navigation/wlm-navigation.service';
import { NotificationRelatedService } from '@common-modules/shared-component/notification-related/notification-related.service';
import { APP_NAVIGATIONS } from '@common-modules/shared/core/injection-tokens/app-navigations.token';
import { APP_PERMISSIONS } from '@common-modules/shared/core/injection-tokens/app-permissions.token';
import { CURRENT_ENVIRONMENT } from '@common-modules/shared/core/injection-tokens/current-environment.token';
import {
  materialFormFieldDefaults,
  materialGlobalRippleConfig,
} from '@common-modules/shared/core/material-defaults';
import { PROVIDERS_INJECTABLE_BY_KEY } from '@common-modules/shared/injectable-by-key/injectable-by-key.decorator';
import { ExternalNavigationService } from '@common-modules/shared/navigation/external-navigation.service';
import { MultiplePipePieChartService } from '@common-modules/shared/pipes/multiple-pipe-pie-chart.service';
import { PipePieChartService } from '@common-modules/shared/pipes/new-pipe-pie-chart.service';
import { UsersIdentityService } from '@common-modules/shared/roles/identity.service';
import { RolesService } from '@common-modules/shared/roles/roles.service';
import { LogsInterfacesService } from '@common-modules/shared/services/logs-interfaces.service';
import { UsersGridODataService } from '@common-modules/shared/services/users/users.service';
import { BaseCustomizableChartComponent } from '@common-modules/wlm-charts/core/base-chart/base-customizable-chart.component';
import { BaseCustomizableChartDataService } from '@common-modules/wlm-charts/core/customizable-chart/base-customizable-chart-data.service';
import { WLCustomizableChartDataService } from '@common-modules/wlm-charts/core/customizable-chart/customizable-chart.service';
import { HistoricalAlarmsGridService } from '@common-modules/wlm-grid/historical-alarms/historical-alarms-grid.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ActiveAlarmsGridService } from './features/alarms/active-alarms-grid/active-alarms.service';
import { ProfilesConfigurationChartService } from './features/alarms/profiles-chart/profiles-configuration-chart.service';
import { ProfilesSaConfigurationChartService } from './features/alarms/profiles-chart/profiles-sa-configuration-chart.service';
import { ActivityCategoryTypesService } from './features/alc/activities-components/activity-type-filter/activity-category-types.service';
import { ActivitiesGridService } from './features/alc/activities-grid/activities-grid.service';
import { ActivityConfirmationService } from './features/alc/activity-confirmation.service';
import { ActivityOperationService } from './features/alc/activity-operation.service';
import { AlcCampaignsService } from './features/alc/campaigns-page/alc-campaigns.service';
import { PrioritisationGridService } from './features/alc/prioritisation-grid/prioritisation-grid.service';
import { BiGridService } from './features/bi/services/bi-grid.service';
import { WlmChartsModule } from './features/charts/wlm-charts.module';
import { AlarmActivationsService } from './features/configuration/alarm-configuration-components/alarm-activations-grid/alarm-activations.service';
import { AlarmRelatedElementsService } from './features/configuration/alarm-configuration-components/alarm-related-elements/alarm-related-elements.service';
import { AlarmsConfigurationService } from './features/configuration/alarms-configuration-page/alarms-configuration.service';
import { PointConfigurationService } from './features/configuration/point-configuration.service';
import { PointValidationGroupService } from './features/configuration/point-validation-group.service';
import { TelemetryPointsService } from './features/configuration/telemetry-points.service';
import { CustomerReadingsGridService } from './features/customer/services/customer-readings-grid.service';
import { HierarchySelectionService } from './features/hierarchy/services/hierarchy-selection.service';
import { IntegrationUserService } from './features/integration/integration-user.service';
import { IntegrationWizardSteps } from './features/integration/pages/integration-page/integration-wizard-steps';
import { LOGS_MODULE_SETTINGS } from './features/logs/logs-module-settings';
import { NotificationService } from './features/monitoring/notification/services/notification.service';
import { PressureMonitoringService } from './features/pressure-management/pressure-monitoring-page/pressure-monitoring.service';
import { WlmCustomizableChartService } from './features/shared/charts/wlm-customizable-chart.service';
import { TransmissionNetworkService } from './features/tn/transmission-network-page/transmission-network.service';
import { WaterBalanceChartService } from './features/water-balance/water-balance-chart/water-balance-chart.service';
import { WaterBalanceService } from './features/water-balance/water-balance-page/water-balance.service';
import { WaterLossAppComponent } from './water-loss-app/water-loss-app.component';
import { waterLossAppNavigations } from './water-loss-navigations';
import { waterLossAppPermissions } from './water-loss-permissions';
import { WaterLossRoutingModule } from './water-loss-routing.module';

// Golden Layoyt requires to have JQuery as global in the window object.
window['$'] = $;

const modules = [
  CommonModule,
  BrowserModule,
  BrowserAnimationsModule,
  WaterLossRoutingModule,
  WlmNavigationModule,
  WlmSharedModule,
  HomeModule,
  DragDropModule,
  RightPanelModule,
  WlmFiltersModule,
  ReduxStateModule,
  DynamicLayoutModule,
  ScrollingModule,
  FormlyModule,
  FormlyMaterialModule,
  FormlyMatDatepickerModule,
  WlmSpinnerModule.forFeature({
    service: WlmSpinnerSplashService,
  }),
  NgxSkeletonLoaderModule.forRoot(),
  WlmChartsModule, // So NGX_ECHARTS_CONFIG can be provided
  DynamicFormsModule, // Because it has forRoot inside
  CommonNavigationModule
];

// Translation json configuration moved to translate-module.configurator.ts
@NgModule({
  imports: modules,
  declarations: [WaterLossAppComponent],
  bootstrap: [WaterLossAppComponent],
  exports: [WaterLossAppComponent, TranslateModule].concat(modules as any),
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpCacheInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DatesInterceptor,
      multi: true,
    },
    HttpCacheManager,
    provideAppInitializer(() => {
      const initializerFn = ((cacheManager: HttpCacheManager) => () => cacheManager.start(1))(inject(HttpCacheManager));
      return initializerFn();
    }),
    HttpCacheClient,
    ...PROVIDERS_INJECTABLE_BY_KEY,
    { provide: MessageService, useClass: KendoMessageService }, // Kendo i18n for all modules.
    { provide: 'MapLayerSourcesService', useExisting: MapLayerSourcesService },
    { provide: 'WlmNavigationService', useExisting: WlmNavigationService },
    { provide: 'AuthenticationService', useExisting: AuthenticationService },
    { provide: 'AuthorizeService', useExisting: AuthorizeService },
    { provide: 'HierarchyTreeFilterHelperService', useExisting: HierarchyTreeFilterHelperService },
    { provide: 'HierarchyService', useExisting: HierarchyService },
    { provide: 'ArrayHelperService', useExisting: ArrayHelperService },
    { provide: 'ColorHelperService', useExisting: ColorHelperService },
    { provide: 'GridHelperService', useExisting: GridHelperService },
    { provide: 'GridSettingsService', useExisting: GridSettingsService },
    { provide: 'NavmenuBuilderHelperService', useExisting: NavMenuBuilderHelperService },
    { provide: 'NotificationPopupService', useExisting: NotificationPopupService },
    { provide: 'IntegrationUserService', useExisting: IntegrationUserService },

    // OData Services
    ActiveAlarmsGridService,
    PipePieChartService,
    TransmissionNetworkService,
    LeaksService,
    UsersGridODataService,
    CustomerReadingsGridService,
    NotificationRelatedService,
    BiGridService,
    AlarmActivationsService,
    AlarmRelatedElementsService,
    HierarchySelectionService,
    PressureMonitoringService,
    AlarmsConfigurationService,
    RolesService,
    PointValidationGroupService,
    UsersIdentityService,
    NotificationService,
    LogsInterfacesService,

    // ALC Services
    ActivityOperationService,
    ActivityConfirmationService,
    ActivityCategoryTypesService,
    ActivitiesGridService,
    PrioritisationGridService,
    AlcCampaignsService,
    HistoricalAlarmsGridService,
    TelemetryPointsService,
    PointConfigurationService,
    PipePieChartService,
    MultiplePipePieChartService,

    // Water Balance Services
    WaterBalanceService,
    WaterBalanceChartService,

    // Config Services
    ProfilesConfigurationChartService,
    ProfilesSaConfigurationChartService,

    RightPanelService,
    ExternalNavigationService,
    NotificationSelectionService,
    UoMService,
    PendingChangesManagerService,
    AuthenticationService,
    {
      provide: DynamicFormParserService,
      useClass: FormlyFormParserService,
    },
    {
      provide: CURRENT_ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: APP_PERMISSIONS,
      useValue: waterLossAppPermissions,
    },
    {
      provide: APP_NAVIGATIONS,
      useValue: waterLossAppNavigations,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: materialFormFieldDefaults,
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: materialGlobalRippleConfig,
    },
    {
      provide: LOGS_MODULE_SETTINGS,
      useValue: {
        integrationGisLayerStepId: IntegrationWizardSteps.GisLayers,
      },
    },
    {
      provide: BaseCustomizableChartComponent,
      useClass: WlmCustomizableChartService
    },
    {
      provide: BaseCustomizableChartDataService,
      useClass: WLCustomizableChartDataService
    }
  ],
})
export class WaterLossModule {
  static injector: Injector;

  constructor(injector: Injector) {
    WaterLossModule.injector = injector;
  }
}
